import React, {
  useState, useCallback, memo,
  useMemo,
} from 'react';
import {
  func, string, shape, array, number, arrayOf,
} from 'prop-types';
import map from 'lodash/map';
import { useSelector, useDispatch } from 'react-redux';
import find from 'lodash/find';
import { useLocale, Locale } from '@active/react-utils/i18n';
import NativeDropdown from '@active/react-ui/NativeDropdown';
import MenuItemList from '@active/react-ui/MenuItemList';
import MenuItem from '@active/react-ui/MenuItem';
import { objectFilter, amountSum } from '../../../../../../../../../../../../../shared/utils/commonUtils';
import { currency } from '../../../../../../../../../../../Currency';
import Installments from '../../../../../../../../../../../Installments';
import { useDeepEffect } from '../../../../../../../../../../../../../shared/utils/customizedHooks';

const PaymentOptionItem = ({
  descriptions = [], options, currencyCode, locale, groupId, caculatePaymentPlan,
}) => {
  const { i18n } = useLocale();
  const {
    paymentPlanIdAndGroups,
  } = useSelector(state => state.paymentOptions);
  const dispatch = useDispatch();
  const [ currentValue, setCurrentValue ] = useState(options?.[0]?.paymentPlanId);
  const [ billingOption, setBillingOption ] = useState('true');

  const handleChange = useCallback((value) => {
    setCurrentValue(value);
    setBillingOption('true');
  }, [ setCurrentValue, setBillingOption ]);

  const handleBillingOptionChange = useCallback((value) => {
    setBillingOption(value);
  }, [ setBillingOption ]);

  useDeepEffect(() => {
    const option = find(options, { paymentPlanId: parseInt(currentValue, 10) }) || options[options.length - 1];
    caculatePaymentPlan({
      paymentPlanObj: objectFilter({
        groupId,
        paymentPlanId: option.paymentPlanId, // Pay in full not have option.paymentPlanId
        autoBilling: option.paymentPlanId ? billingOption === 'true' : undefined,
      }),
      currentAmountObj: {
        groupId,
        currentAmount: option.deposit,
      },
    });
  }, [ currentValue, billingOption, dispatch, options, groupId, caculatePaymentPlan ]);

  const datas = useMemo(() => options.map(({ paymentPlanId, installments: optionsInstallments }) => {
    return ({
      text: paymentPlanId ? i18n('checkout.lib.components.text.depositPayment', { 0: optionsInstallments.length }) : i18n('checkout.lib.components.text.payInFull'),
      value: `${paymentPlanId}`,
    });
  }), [ i18n, options ]);

  const currentOption = useMemo(
    () => (find(options, { paymentPlanId: parseInt(currentValue, 10) }) || options[options.length - 1]),
    [ currentValue, options ]);

  const { installments: ppfInstallments, amount: ppfAmount } = useMemo(
    () => find(paymentPlanIdAndGroups, { groupId }) || {},
    [ paymentPlanIdAndGroups, groupId ]);

  const { installments, autoBillingRequired, deposit } = currentOption;

  const displayInstallments = useMemo(() => ppfInstallments || installments,
    [ installments, ppfInstallments ]);

  const currentAmount = useMemo(
    () => currencyCode && currency({ amount: amountSum(deposit, ppfAmount), code: currencyCode, locale }),
    [ currencyCode, deposit, locale, ppfAmount ]);

  const predictedPPF = useMemo(() => {
    let predictedAmount = 0;
    if (ppfInstallments) {
      map(ppfInstallments, (c) => {
        predictedAmount = amountSum(predictedAmount, c.amount);
      });
    }
    return predictedAmount;
  }, [ ppfInstallments ]);

  return (
    <div className="payment-option-item">
      <div className='payment-option-item-desc'>
        { descriptions.length
          && <div className='paymentOption-content__descs'>
            {
              descriptions.map((dsc, idx) => <div className="paymentOption-content__desc" key={idx}>{dsc}</div>)
            }
          </div>
        }
        <div className="paymentOption-content__amout">
          <div className='amount-due-now_amount'>{currentAmount}</div>
          <div className='amount-due-now'><Locale id="checkout.lib.components.text.amountDueNow" /></div>
        </div>
      </div>
      <div className="paymentOption-content__paymentPlanLabel">
        <Locale id="checkout.lib.components.text.choosePaymentPlan" />
      </div>
      { predictedPPF > 0
      && <div className='paymentOption-content__info'>
        <i className='ic-info-r'/>
        <div className='paymentOption-content__info-content'>
          <Locale id="checkout.lib.components.text.futureInstallments" />
        </div>
      </div>
      }
      <NativeDropdown
        name={groupId}
        value={`${currentValue}`}
        onChange={handleChange}
        aria-label={i18n('checkout.lib.components.text.choosePaymentPlan')}>
        <MenuItemList>
          {datas.map(item => (<MenuItem value={item.value} key={item.value}>{item.text}</MenuItem>))}
        </MenuItemList>
      </NativeDropdown>
      <Installments installmentList={displayInstallments} />
      {autoBillingRequired !== undefined && <>
        {// autoBillingRequired undefined is pay in full case.
          autoBillingRequired
            ? <div className="payment-option-item__autoBilling">
              <Locale id="checkout.lib.components.text.paymentWillTakeAutomatically" />
            </div>
            : <>
              <div className="payment-option-item__billingOptionLabel">
                <Locale id="checkout.lib.components.text.selectBillingOption" />
              </div>
              <NativeDropdown
                name="billingOption"
                value={billingOption}
                onChange={handleBillingOptionChange}
                aria-label={i18n('checkout.lib.components.text.selectBillingOption')}>
                <MenuItemList>
                  <MenuItem value="true">{i18n('checkout.lib.components.text.billMeAuto')}</MenuItem>
                  <MenuItem value="false">{i18n('checkout.lib.components.text.payBalanceManually')}</MenuItem>
                </MenuItemList>
              </NativeDropdown>
            </>
        }
      </>}
    </div>);
};
PaymentOptionItem.propTypes = {
  options: arrayOf(shape({
    paymentPlanId: number,
    installments: array,
  })),
  descriptions: arrayOf(string),
  currencyCode: string.isRequired,
  i18n: func.isRequired,
  locale: string,
  onChange: func.isRequired,
  groupId: string.isRequired,
};

PaymentOptionItem.defaultProps = {
  options: [],
  descriptions: [],
};

export default memo(PaymentOptionItem);
